
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import hostlink from "../Hostlink/hostlink";
import "./TailorReceive.css";

const TailorReceive = () => {

    const today = new Date().toISOString().split("T")[0];

    // =====================================================
    // STATE
    // =====================================================

    const [tailors, setTailors] = useState([]);
    const [pendingData, setPendingData] = useState([]);
    const [receivedData, setReceivedData] = useState([]);

    const [selectedTailor, setSelectedTailor] = useState(null);

    const [receiveDate, setReceiveDate] = useState(today);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [showNewEntry, setShowNewEntry] = useState(false);

    // Pending search
    const [search, setSearch] = useState("");

    // Received search
    const [receivedSearch, setReceivedSearch] = useState("");

    // Date filters
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);

    // Edit
    const [editingId, setEditingId] = useState(null);


    // =====================================================
    // SHOP ID
    // =====================================================

    const shopid = localStorage.getItem("MYPCKARTUSER")
        ? JSON.parse(localStorage.getItem("MYPCKARTUSER"))[0].id
        : "0";


    // =====================================================
    // FETCH TAILORS
    // =====================================================

    const fetchTailors = async () => {

        try {

            const response = await axios.get(
                `${hostlink}/${shopid}/TailorMasCont`
            );

            console.log("Tailors:", response.data);

            setTailors(response.data || []);

        } catch (error) {

            console.error("Tailor fetch error:", error);

            toast.error(
                error.response?.data ||
                "Unable to load tailor list"
            );
        }
    };


    // =====================================================
    // FETCH PENDING
    // =====================================================

    const fetchPending = async (tailorcode) => {

        if (!tailorcode) {

            setPendingData([]);

            return;
        }

        try {

            setLoading(true);

            const response = await axios.get(
                `${hostlink}/api/tailor/pending`,
                {
                    params: {
                        shopid: shopid,
                        tailorcode: tailorcode
                    }
                }
            );

            console.log("Pending:", response.data);

            const data = response.data || [];

            const formatted = data.map(row => ({
                ...row,
                receiveqty: "",
                rate: row.rate ?? ""
            }));

            setPendingData(formatted);

        } catch (error) {

            console.error("Pending fetch error:", error);

            toast.error(
                error.response?.data ||
                "Unable to load pending work"
            );

            setPendingData([]);

        } finally {

            setLoading(false);
        }
    };


    // =====================================================
    // FETCH RECEIVED
    // =====================================================

    const fetchReceived = async () => {

        try {

            const response = await axios.get(
                `${hostlink}/api/tailor/receive`,
                {
                    params: {
                        shopid: shopid
                    }
                }
            );

            console.log("Received:", response.data);

            setReceivedData(response.data || []);

        } catch (error) {

            console.error("Received fetch error:", error);

            toast.error(
                error.response?.data ||
                "Unable to load received data"
            );
        }
    };


    // =====================================================
    // INITIAL LOAD
    // =====================================================

    useEffect(() => {

        if (Number(shopid) !== 0) {

            fetchTailors();
            fetchReceived();
        }

    }, [shopid]);


    // =====================================================
    // NEW ENTRY
    // =====================================================

    const newEntry = () => {

        setShowNewEntry(true);

        setSelectedTailor(null);

        setPendingData([]);

        setReceiveDate(today);

        setEditingId(null);

        setSearch("");
    };


    // =====================================================
    // CLOSE ENTRY
    // =====================================================

    const closeEntry = () => {

        setShowNewEntry(false);

        setSelectedTailor(null);

        setPendingData([]);

        setEditingId(null);

        setSearch("");
    };


    // =====================================================
    // SELECT TAILOR
    // =====================================================

    const handleTailorChange = (e) => {

        const code = Number(e.target.value);

        if (!code) {

            setSelectedTailor(null);
            setPendingData([]);

            return;
        }

        const tailor =
            tailors.find(
                t =>
                    Number(
                        t.tailorcode ??
                        t.id ??
                        t.code
                    ) === code
            );

        setSelectedTailor(tailor || null);

        fetchPending(code);
    };


    // =====================================================
    // RECEIVE QTY CHANGE
    // =====================================================

    const changeReceiveQty = (index, value) => {

        setPendingData(prev => {

            const copy = [...prev];

            copy[index] = {
                ...copy[index],
                receiveqty: value
            };

            return copy;
        });
    };


    // =====================================================
    // RATE CHANGE
    // =====================================================

    const changeRate = (index, value) => {

        setPendingData(prev => {

            const copy = [...prev];

            copy[index] = {
                ...copy[index],
                rate: value
            };

            return copy;
        });
    };


    // =====================================================
    // TOTAL RECEIVE QTY
    // =====================================================

    const totalReceiveQty = useMemo(() => {

        return pendingData.reduce(
            (sum, row) =>
                sum + (Number(row.receiveqty) || 0),
            0
        );

    }, [pendingData]);


    // =====================================================
    // TOTAL AMOUNT
    // =====================================================

    const totalAmount = useMemo(() => {

        return pendingData.reduce(
            (sum, row) => {

                const qty =
                    Number(row.receiveqty) || 0;

                const rate =
                    Number(row.rate) || 0;

                return sum + qty * rate;

            },
            0
        );

    }, [pendingData]);


    // =====================================================
    // FILTER PENDING
    // =====================================================

    const filteredPending =
        pendingData.filter(row => {

            const text =
                search.toLowerCase().trim();

            if (!text) {
                return true;
            }

            return (

                String(row.billno || "")
                    .toLowerCase()
                    .includes(text)

                ||

                String(row.style || "")
                    .toLowerCase()
                    .includes(text)

                ||

                String(row.size || "")
                    .toLowerCase()
                    .includes(text)

            );
        });


    // =====================================================
    // SAVE
    // =====================================================

    const saveReceive = async () => {

        if (!selectedTailor) {

            toast.error("Please select tailor");

            return;
        }


        if (!receiveDate) {

            toast.error("Please select receive date");

            return;
        }


        const items =
            pendingData
                .filter(
                    row =>
                        Number(row.receiveqty) > 0
                )
                .map(row => ({

                    purchaseId:
                        Number(
                            row.purchaseId ||
                            row.id
                        ),

                    billno:
                        row.billno || "",

                    style:
                        row.style || "",

                    size:
                        row.size || "",

                    totalpcs:
                        Number(
                            row.totalpcs ||
                            row.pcs ||
                            0
                        ),

                    pendingpcs:
                        Number(
                            row.pendingpcs ||
                            0
                        ),

                    receiveqty:
                        Number(
                            row.receiveqty
                        ),

                    rate:
                        Number(
                            row.rate || 0
                        )
                }));


        if (items.length === 0) {

            toast.error(
                "Enter receive quantity for at least one lot"
            );

            return;
        }


        // =================================================
        // FRONTEND VALIDATION
        // =================================================

        for (const item of items) {

            if (
                item.receiveqty >
                item.pendingpcs
            ) {

                toast.error(
                    `Receive quantity cannot be greater than pending quantity for ${item.billno}`
                );

                return;
            }

            if (item.receiveqty <= 0) {

                toast.error(
                    `Invalid receive quantity for ${item.billno}`
                );

                return;
            }
        }


        const tailorcode =
            Number(
                selectedTailor.tailorcode ??
                selectedTailor.id ??
                selectedTailor.code
            );


        const tailorname =
            selectedTailor.tailorname ||
            selectedTailor.tname ||
            selectedTailor.name ||
            "";


        if (!tailorcode) {

            toast.error("Invalid tailor");

            return;
        }


        if (!tailorname) {

            toast.error("Tailor name is required");

            return;
        }


        const payload = {

            tailorcode: tailorcode,

            tailorname: tailorname,

            receivedate: receiveDate,

            items: items
        };


        try {

            setSaving(true);


            // =================================================
            // EDIT
            // =================================================

            if (editingId) {

                await axios.put(
                    `${hostlink}/api/tailor/receive/${editingId}`,
                    payload
                );

                toast.success(
                    "Receive entry updated successfully"
                );

            }

            // =================================================
            // NEW SAVE
            // =================================================

            else {

                await axios.post(
                    `${hostlink}/api/tailor/receive`,
                    payload
                );

                toast.success(
                    "Tailor receive saved successfully"
                );
            }


            // =================================================
            // REFRESH RECEIVED LIST
            // =================================================

            await fetchReceived();


            // =================================================
            // CLOSE NEW ENTRY FORM
            // =================================================

            setShowNewEntry(false);

            setSelectedTailor(null);

            setPendingData([]);

            setReceiveDate(today);

            setEditingId(null);

            setSearch("");


        } catch (error) {

            console.error(
                "Save receive error:",
                error
            );

            toast.error(
                error.response?.data ||
                "Unable to save receive"
            );

        } finally {

            setSaving(false);
        }
    };


    // =====================================================
    // EDIT
    // =====================================================

    const editReceive = (row) => {

        setShowNewEntry(true);

        setEditingId(row.id);

        setReceiveDate(
            row.receivedate
                ? String(row.receivedate)
                    .substring(0, 10)
                : today
        );


        const tailor =
            tailors.find(
                t =>
                    Number(
                        t.tailorcode ??
                        t.id ??
                        t.code
                    ) ===
                    Number(row.tailorcode)
            );


        setSelectedTailor(
            tailor || {

                tailorcode:
                    row.tailorcode,

                tailorname:
                    row.tailorname,

                name:
                    row.tailorname
            }
        );


        setPendingData([{

            purchaseId:
                row.purchaseId,

            billno:
                row.billno,

            style:
                row.style,

            size:
                row.size,

            totalpcs:
                row.totalpcs || 0,

            pendingpcs:
                row.pendingpcs ||
                row.receiveqty ||
                0,

            receiveqty:
                row.receiveqty,

            rate:
                row.rate
        }]);


        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };


    // =====================================================
    // DELETE
    // =====================================================

    const deleteReceive = async (id) => {

        const confirmDelete =
            window.confirm(
                "Are you sure you want to delete this entry?"
            );

        if (!confirmDelete) {
            return;
        }


        try {

            await axios.delete(
                `${hostlink}/api/tailor/receive/${id}`,
                {
                    params: {
                        shopid: shopid
                    }
                }
            );


            toast.success(
                "Receive entry deleted successfully"
            );


            await fetchReceived();


            if (selectedTailor) {

                await fetchPending(
                    Number(
                        selectedTailor.tailorcode ??
                        selectedTailor.id ??
                        selectedTailor.code
                    )
                );
            }


        } catch (error) {

            console.error(
                "Delete error:",
                error
            );

            toast.error(
                error.response?.data ||
                "Unable to delete entry"
            );
        }
    };


    // =====================================================
    // FILTER RECEIVED
    // =====================================================

    const filteredReceived =
        receivedData.filter(row => {

            const text =
                receivedSearch
                    .toLowerCase()
                    .trim();


            const searchMatch =

                !text

                ||

                String(row.billno || "")
                    .toLowerCase()
                    .includes(text)

                ||

                String(row.style || "")
                    .toLowerCase()
                    .includes(text)

                ||

                String(row.size || "")
                    .toLowerCase()
                    .includes(text)

                ||

                String(row.tailorname || "")
                    .toLowerCase()
                    .includes(text);


            const date =
                row.receivedate
                    ? String(
                        row.receivedate
                    ).substring(0, 10)
                    : "";


            const fromMatch =
                !fromDate ||
                date >= fromDate;


            const toMatch =
                !toDate ||
                date <= toDate;


            return (
                searchMatch &&
                fromMatch &&
                toMatch
            );
        });


    // =====================================================
    // UI
    // =====================================================

    return (

        <div className="tailor-container">

            <ToastContainer />


            {/* =================================================
                HEADER
            ================================================= */}

            <div className="tailor-header">

                <div>

                    <h2>
                        Tailor Receive
                    </h2>

                    <p>
                        Manage pending and received tailor work
                    </p>

                </div>


                {!showNewEntry && (

                    <button
                        className="new-btn"
                        onClick={newEntry}
                    >
                        + New Entry
                    </button>

                )}

            </div>


            {/* =================================================
                NEW ENTRY
            ================================================= */}

            {showNewEntry && (

                <div className="receive-card">

                    <div className="receive-card-header">

                        <div>

                            <h4>
                                {editingId
                                    ? "Edit Receive"
                                    : "New Tailor Receive"}
                            </h4>

                            <span>
                                Select tailor and enter received quantity
                            </span>

                        </div>


                        <button
                            className="close-btn"
                            onClick={closeEntry}
                        >
                            ×
                        </button>

                    </div>


                    <div className="receive-form">

                        {/* Tailor */}

                        <div className="form-group">

                            <label>
                                Tailor
                            </label>

                            <select
                                className="form-control"
                                value={
                                    selectedTailor
                                        ? Number(
                                            selectedTailor.tailorcode ??
                                            selectedTailor.id ??
                                            selectedTailor.code
                                        )
                                        : ""
                                }
                                onChange={
                                    handleTailorChange
                                }
                            >

                                <option value="">
                                    Select Tailor
                                </option>


                                {tailors.map(
                                    (tailor, index) => (

                                        <option
                                            key={
                                                tailor.tailorcode ??
                                                tailor.id ??
                                                index
                                            }
                                            value={
                                                tailor.tailorcode ??
                                                tailor.id ??
                                                tailor.code
                                            }
                                        >

                                            {tailor.tailorname ||
                                                tailor.tname ||
                                                tailor.name}

                                        </option>

                                    )
                                )}

                            </select>

                        </div>


                        {/* Date */}

                        <div className="form-group">

                            <label>
                                Receive Date
                            </label>

                            <input
                                type="date"
                                className="form-control"
                                value={receiveDate}
                                onChange={
                                    e =>
                                        setReceiveDate(
                                            e.target.value
                                        )
                                }
                            />

                        </div>


                        {/* Total Qty */}

                        <div className="summary-box">

                            <span>
                                Receive Qty
                            </span>

                            <strong>
                                {totalReceiveQty.toFixed(2)}
                            </strong>

                        </div>


                        {/* Total Amount */}

                        <div className="summary-box">

                            <span>
                                Amount
                            </span>

                            <strong>
                                ₹ {totalAmount.toFixed(2)}
                            </strong>

                        </div>

                    </div>


                    {/* =================================================
                        PENDING TABLE
                    ================================================= */}

                    <div className="pending-section">

                        <div className="section-top">

                            <div>

                                <h5>
                                    Pending Work
                                </h5>

                                {selectedTailor && (

                                    <span className="tailor-label">

                                        {selectedTailor.tailorname ||
                                            selectedTailor.tname ||
                                            selectedTailor.name}

                                    </span>

                                )}

                            </div>


                            <input
                                type="text"
                                className="search-input"
                                placeholder="Search Lot / Style / Size..."
                                value={search}
                                onChange={
                                    e =>
                                        setSearch(
                                            e.target.value
                                        )
                                }
                            />

                        </div>


                        <div className="table-wrapper">

                            <table>

                                <thead>

                                    <tr>

                                        <th>
                                            #
                                        </th>

                                        <th>
                                            Lot No
                                        </th>

                                        <th>
                                            Style
                                        </th>

                                        <th>
                                            Size
                                        </th>

                                        <th>
                                            Total Pcs
                                        </th>

                                        <th>
                                            Pending Pcs
                                        </th>

                                        <th>
                                            Receive Qty
                                        </th>

                                        <th>
                                            Rate
                                        </th>

                                        <th>
                                            Amount
                                        </th>

                                    </tr>

                                </thead>


                                <tbody>

                                    {!selectedTailor ? (

                                        <tr>

                                            <td
                                                colSpan="9"
                                                className="empty"
                                            >
                                                Select a tailor to see
                                                pending work
                                            </td>

                                        </tr>

                                    ) : loading ? (

                                        <tr>

                                            <td
                                                colSpan="9"
                                                className="empty"
                                            >
                                                Loading...
                                            </td>

                                        </tr>

                                    ) : filteredPending.length === 0 ? (

                                        <tr>

                                            <td
                                                colSpan="9"
                                                className="empty"
                                            >
                                                No pending work
                                            </td>

                                        </tr>

                                    ) : (

                                        filteredPending.map(
                                            (row, index) => {

                                                const qty =
                                                    Number(
                                                        row.receiveqty
                                                    ) || 0;

                                                const rate =
                                                    Number(
                                                        row.rate
                                                    ) || 0;

                                                const originalIndex =
                                                    pendingData.indexOf(row);

                                                return (

                                                    <tr
                                                        key={
                                                            row.purchaseId ||
                                                            row.id ||
                                                            index
                                                        }
                                                    >

                                                        <td>
                                                            {index + 1}
                                                        </td>

                                                        <td>
                                                            <strong>
                                                                {row.billno}
                                                            </strong>
                                                        </td>

                                                        <td>
                                                            {row.style}
                                                        </td>

                                                        <td>
                                                            {row.size}
                                                        </td>

                                                        <td>
                                                            {Number(
                                                                row.totalpcs ||
                                                                row.pcs ||
                                                                0
                                                            ).toFixed(2)}
                                                        </td>

                                                        <td>

                                                            <span className="pending-badge">

                                                                {Number(
                                                                    row.pendingpcs ||
                                                                    0
                                                                ).toFixed(2)}

                                                            </span>

                                                        </td>

                                                        <td>

                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max={
                                                                    row.pendingpcs
                                                                }
                                                                step="0.01"
                                                                className="qty-input"
                                                                value={
                                                                    row.receiveqty
                                                                }
                                                                onChange={
                                                                    e =>
                                                                        changeReceiveQty(
                                                                            originalIndex,
                                                                            e.target.value
                                                                        )
                                                                }
                                                                placeholder="0"
                                                            />

                                                        </td>

                                                        <td>

                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                className="rate-input"
                                                                value={
                                                                    row.rate
                                                                }
                                                                onChange={
                                                                    e =>
                                                                        changeRate(
                                                                            originalIndex,
                                                                            e.target.value
                                                                        )
                                                                }
                                                            />

                                                        </td>

                                                        <td>

                                                            ₹{" "}
                                                            {(
                                                                qty *
                                                                rate
                                                            ).toFixed(2)}

                                                        </td>

                                                    </tr>

                                                );
                                            }
                                        )

                                    )}

                                </tbody>

                            </table>

                        </div>

                    </div>


                    {/* =================================================
                        BUTTONS
                    ================================================= */}

                    <div className="form-actions">

                        <button
                            className="cancel-btn"
                            onClick={closeEntry}
                        >
                            Cancel
                        </button>


                        <button
                            className="save-btn"
                            onClick={saveReceive}
                            disabled={saving}
                        >

                            {saving
                                ? "Saving..."
                                : editingId
                                    ? "Update Receive"
                                    : "Save Receive"}

                        </button>

                    </div>

                </div>

            )}


            {/* =================================================
                RECEIVED ENTRIES
            ================================================= */}

            <div className="receive-card">

                <div className="section-top">

                    <div>

                        <h4>
                            Received Entries
                        </h4>

                        <span className="record-count">
                            {filteredReceived.length} Records
                        </span>

                    </div>


                    <div className="filters">

                        <input
                            type="date"
                            value={fromDate}
                            onChange={
                                e =>
                                    setFromDate(
                                        e.target.value
                                    )
                            }
                        />

                        <span>
                            to
                        </span>

                        <input
                            type="date"
                            value={toDate}
                            onChange={
                                e =>
                                    setToDate(
                                        e.target.value
                                    )
                            }
                        />


                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search Tailor / Lot / Style / Size..."
                            value={receivedSearch}
                            onChange={
                                e =>
                                    setReceivedSearch(
                                        e.target.value
                                    )
                            }
                        />

                    </div>

                </div>


                <div className="table-wrapper">

                    <table>

                        <thead>

                            <tr>

                                <th>
                                    #
                                </th>

                                <th>
                                    Receive Date
                                </th>

                                <th>
                                    Lot No
                                </th>

                                <th>
                                    Style
                                </th>

                                <th>
                                    Size
                                </th>

                                <th>
                                    Tailor
                                </th>

                                <th>
                                    Qty
                                </th>

                                <th>
                                    Rate
                                </th>

                                <th>
                                    Amount
                                </th>

                                <th>
                                    Action
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {filteredReceived.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan="10"
                                        className="empty"
                                    >
                                        No received entries
                                    </td>

                                </tr>

                            ) : (

                                filteredReceived.map(
                                    (row, index) => (

                                        <tr
                                            key={row.id}
                                        >

                                            <td>
                                                {index + 1}
                                            </td>

                                            <td>

                                                {row.receivedate
                                                    ? String(
                                                        row.receivedate
                                                    ).substring(0, 10)
                                                    : ""}

                                            </td>

                                            <td>

                                                <strong>
                                                    {row.billno}
                                                </strong>

                                            </td>

                                            <td>
                                                {row.style}
                                            </td>

                                            <td>
                                                {row.size}
                                            </td>

                                            <td>

                                                <span className="tailor-name">

                                                    {row.tailorname}

                                                </span>

                                            </td>

                                            <td>

                                                {Number(
                                                    row.receiveqty ||
                                                    0
                                                ).toFixed(2)}

                                            </td>

                                            <td>

                                                {Number(
                                                    row.rate ||
                                                    0
                                                ).toFixed(2)}

                                            </td>

                                            <td>

                                                <strong>

                                                    ₹{" "}
                                                    {Number(
                                                        row.amount ||
                                                        0
                                                    ).toFixed(2)}

                                                </strong>

                                            </td>

                                            <td>

                                                <div className="actions">

                                                    <button
                                                        className="edit-btn"
                                                        onClick={() =>
                                                            editReceive(
                                                                row
                                                            )
                                                        }
                                                    >
                                                        Edit
                                                    </button>


                                                    <button
                                                        className="delete-btn"
                                                        onClick={() =>
                                                            deleteReceive(
                                                                row.id
                                                            )
                                                        }
                                                    >
                                                        Delete
                                                    </button>

                                                </div>

                                            </td>

                                        </tr>

                                    )
                                )

                            )}

                        </tbody>

                    </table>

                </div>

            </div>

        </div>
    );
};

export default TailorReceive;

